/**
 * Airwallex Apple Pay Adapter
 *
 * Uses native ApplePaySession API directly (not Stripe.js) to show the payment sheet
 * and extract the encrypted payment token for Airwallex processing.
 *
 * Flow:
 * 1. initialize() - Checks if ApplePaySession is available
 * 2. canMakePayment() - Checks if user can pay with Apple Pay
 * 3. showPaymentSheet() - Shows Apple Pay sheet with merchant validation callback
 *
 * The key difference from Stripe's approach is that we use the native
 * ApplePaySession API and handle merchant validation via Airwallex's
 * payment_session/start API which validates with Apple.
 *
 * Mock scenarios are supported for E2E testing without real Apple Pay.
 */

/// <reference types="@types/applepayjs" />

import { AnalyticsService } from "../analytics";
import { type AddressPayload, fromApplePayContact } from "../utils/wallet-billing-address";

export enum AirwallexApplePayMockScenario {
  None = "none",
  Success = "success",
  Cancelled = "cancelled",
}

// Apple Pay token structure from payment.token
export interface ApplePayEncryptedToken {
  paymentData: {
    version?: string;
    data: string;
    signature: string;
    header: {
      ephemeralPublicKey: string;
      publicKeyHash: string;
      transactionId: string;
    };
  };
  paymentMethod: {
    displayName: string;
    network: string;
    type: string;
  };
  transactionIdentifier: string;
}

export interface AirwallexApplePayConfig {
  amount: number; // Amount in atomic units (cents)
  currency: string; // e.g., "USD"
  country: string; // e.g., "US"
  merchantName?: string; // Display name on Apple Pay sheet
  // Whether the merchant collects a billing address (/session-info). Missing
  // (older backend, or no /session-info call) means true (PAY-1953).
  collectBillingAddress?: boolean;
  // Ask the sheet for a shipping address (PaymentKit.js `requestShippingAddress`).
  requestShippingAddress?: boolean;
}

export type AirwallexApplePayShowResult =
  | {
      success: true;
      token: ApplePayEncryptedToken;
      payerEmail?: string;
      payerFirstName?: string;
      payerLastName?: string;
      payerBillingAddress?: AddressPayload;
      // Present only when the sheet was asked for one (`requestShippingAddress`).
      payerShippingAddress?: AddressPayload;
      complete: (status: "success" | "fail") => void;
    }
  | { success: false; cancelled: true }
  | { success: false; error: string };

// Merchant validation callback type - called during onvalidatemerchant
export type MerchantValidationCallback = (
  validationUrl: string,
) => Promise<{ merchantSession?: object; error?: string }>;

export class AirwallexApplePayAdapter {
  private config: AirwallexApplePayConfig | null = null;
  private mockScenario: AirwallexApplePayMockScenario;

  constructor(mockScenario?: AirwallexApplePayMockScenario) {
    this.mockScenario = mockScenario ?? AirwallexApplePayMockScenario.None;
  }

  /**
   * Initialize the adapter with payment configuration.
   * Returns true if Apple Pay is available on this device.
   */
  initialize(config: AirwallexApplePayConfig): boolean {
    // Mock scenarios bypass real Apple Pay initialization
    if (this.mockScenario !== AirwallexApplePayMockScenario.None) {
      console.log("[MockApplePay:Airwallex] initialize called (mock mode)");
      this.config = config;
      return true;
    }

    // Check if ApplePaySession is available (Safari on iOS/macOS only)
    if (typeof window === "undefined" || !window.ApplePaySession) {
      console.error("[ApplePay:Airwallex] ApplePaySession not available (not Safari or unsupported device)");
      return false;
    }

    this.config = config;
    return true;
  }

  /**
   * Check if the user can make a payment with Apple Pay.
   * Returns true if Apple Pay is set up on this device.
   *
   * Kept async for external callers of this published subpath. The SDK's own tap
   * path uses canMakePaymentSync() instead.
   */
  async canMakePayment(): Promise<boolean> {
    return this.canMakePaymentSync();
  }

  /**
   * Synchronous form of canMakePayment().
   *
   * Synchronous on purpose: it runs inside the tap on the no-prepare fallback, and
   * any `await` before `new ApplePaySession()` can revoke the user gesture (PAY-2280).
   */
  canMakePaymentSync(): boolean {
    // Mock scenarios always return true
    if (this.mockScenario !== AirwallexApplePayMockScenario.None) {
      console.log("[MockApplePay:Airwallex] canMakePayment: true (mock mode)");
      return true;
    }

    if (!window.ApplePaySession) {
      return false;
    }

    try {
      const canMake = window.ApplePaySession.canMakePayments();
      console.log("[ApplePay:Airwallex] canMakePayments:", canMake);
      return canMake;
    } catch (error) {
      console.error("[ApplePay:Airwallex] canMakePayment error:", error);
      return false;
    }
  }

  /**
   * Convert atomic units (cents) to display format for Apple Pay.
   * Apple Pay expects a string like "10.00" for $10.00.
   */
  private formatAmount(amountAtom: number, currency: string): string {
    // Most currencies use 2 decimal places, some like JPY use 0
    const zeroDecimalCurrencies = [
      "BIF",
      "CLP",
      "DJF",
      "GNF",
      "ISK",
      "JPY",
      "KMF",
      "KRW",
      "PYG",
      "RWF",
      "UGX",
      "VND",
      "VUV",
      "XAF",
      "XOF",
      "XPF",
    ];
    const isZeroDecimal = zeroDecimalCurrencies.includes(currency.toUpperCase());

    if (isZeroDecimal) {
      return amountAtom.toString();
    }

    return (amountAtom / 100).toFixed(2);
  }

  /**
   * Show the Apple Pay payment sheet and return the encrypted token.
   *
   * This uses the native ApplePaySession API with a two-step flow:
   * 1. onvalidatemerchant - Called by Apple to validate the merchant
   *    The callback should call our backend which calls Airwallex's payment_session/start
   * 2. onpaymentauthorized - Called when user authorizes with Face ID/Touch ID
   *    Returns the encrypted token for processing
   *
   * @param onMerchantValidation - Callback to validate merchant with backend
   * @returns Promise with token or error
   */
  async showPaymentSheet(onMerchantValidation: MerchantValidationCallback): Promise<AirwallexApplePayShowResult> {
    // Handle mock scenarios
    if (this.mockScenario === AirwallexApplePayMockScenario.Success) {
      console.log("[MockApplePay:Airwallex] showPaymentSheet: returning mock success token");
      const mockToken: ApplePayEncryptedToken = {
        paymentData: {
          version: "EC_v1",
          data: "MOCK_ENCRYPTED_DATA",
          signature: "MOCK_SIGNATURE",
          header: {
            ephemeralPublicKey: "MOCK_PUBLIC_KEY",
            publicKeyHash: "MOCK_HASH",
            transactionId: "mock_txn_123",
          },
        },
        paymentMethod: {
          displayName: "Visa 4242",
          network: "Visa",
          type: "debit",
        },
        transactionIdentifier: "MOCK_TXN_ID",
      };
      return {
        success: true,
        token: mockToken,
        payerEmail: "mock@example.com",
        payerFirstName: "Mock",
        payerLastName: "User",
        complete: (status) => console.log(`[MockApplePay:Airwallex] complete: ${status}`),
      };
    }

    if (this.mockScenario === AirwallexApplePayMockScenario.Cancelled) {
      console.log("[MockApplePay:Airwallex] showPaymentSheet: returning mock cancelled");
      return { success: false, cancelled: true };
    }

    // Real Apple Pay flow
    if (!this.config) {
      return { success: false, error: "Apple Pay not initialized" };
    }

    if (!window.ApplePaySession) {
      return { success: false, error: "ApplePaySession not available" };
    }

    const config = this.config;
    const amount = this.formatAmount(config.amount, config.currency);

    // Create the Apple Pay payment request
    const paymentRequest: ApplePayJS.ApplePayPaymentRequest = {
      countryCode: config.country.toUpperCase(),
      currencyCode: config.currency.toUpperCase(),
      supportedNetworks: ["visa", "masterCard", "amex", "discover"],
      merchantCapabilities: ["supports3DS"],
      total: {
        label: config.merchantName || "Total",
        amount: amount,
        type: "final",
      },
      // Surface the payer's email and name (PAY-1089) plus billing address incl. zip
      // (PAY-1953) so the backend can fill missing customer info. Apple never returns
      // email on the billing contact and the billing name comes from the card's billing
      // address (often empty), so the shipping contact carries the Apple ID name/email —
      // request both contacts. postalAddress is the only way to get the zip (Apple has no
      // zip-only field); asked only when the merchant collects a billing address.
      requiredBillingContactFields:
        config.collectBillingAddress === false ? ["email", "name"] : ["email", "name", "postalAddress"],
      // The shipping contact already carries the Apple ID name/email (above); postalAddress
      // adds the shipping address, only when the merchant's integration asked for one.
      requiredShippingContactFields: config.requestShippingAddress
        ? ["email", "name", "postalAddress"]
        : ["email", "name"],
    };

    return new Promise((resolve) => {
      try {
        // Create ApplePaySession (version 3 is widely supported)
        // biome-ignore lint/style/noNonNullAssertion: ApplePaySession is guaranteed to exist (checked in canMakePayments)
        const session = new window.ApplePaySession!(3, paymentRequest);

        // Capture status constants at creation time (before optional chaining could yield undefined)
        // biome-ignore lint/style/noNonNullAssertion: ApplePaySession existence verified above
        const STATUS_SUCCESS = window.ApplePaySession!.STATUS_SUCCESS;
        // biome-ignore lint/style/noNonNullAssertion: ApplePaySession existence verified above
        const STATUS_FAILURE = window.ApplePaySession!.STATUS_FAILURE;

        // Where the session is in its lifecycle, for the cancel telemetry below.
        let phase: "validating_merchant" | "awaiting_authorization" | "authorized" = "validating_merchant";
        // The sheet can be cancelled (by the user, or by Apple) while our merchant
        // validation fetch is still in flight. Once it is, every session call throws
        // a bare `InvalidAccessError` (WebKit's canAbort/canComplete* guards), and the
        // throw from inside an async handler escapes as an unhandled rejection. Guard
        // every session call so a dead session resolves cleanly instead of crashing.
        let cancelled = false;
        let settled = false;
        const settle = (result: AirwallexApplePayShowResult) => {
          if (settled) return;
          settled = true;
          resolve(result);
        };
        const safeAbort = (reason: string) => {
          if (cancelled) {
            console.warn(`[ApplePay:Airwallex] Skipping abort (${reason}): sheet already closed`);
            return;
          }
          try {
            session.abort();
          } catch (error) {
            console.warn(`[ApplePay:Airwallex] abort() rejected (${reason}): session no longer active`, error);
          }
        };

        // Handle merchant validation
        session.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
          console.log("[ApplePay:Airwallex] onvalidatemerchant called", event.validationURL);

          try {
            const validationResult = await onMerchantValidation(event.validationURL);

            if (cancelled) {
              // The user (or Apple) closed the sheet while /start was in flight. The
              // oncancel handler already resolved; there is no session left to complete.
              console.warn("[ApplePay:Airwallex] Sheet closed during merchant validation; dropping result");
              return;
            }

            if (validationResult.error || !validationResult.merchantSession) {
              console.error("[ApplePay:Airwallex] Merchant validation failed:", validationResult.error);
              safeAbort("merchant validation failed");
              settle({
                success: false,
                error: validationResult.error || "Merchant validation failed",
              });
              return;
            }

            // Complete merchant validation with the session from Apple
            session.completeMerchantValidation(validationResult.merchantSession);
            phase = "awaiting_authorization";
            console.log("[ApplePay:Airwallex] Merchant validation completed");
          } catch (error) {
            console.error("[ApplePay:Airwallex] Merchant validation error:", error);
            safeAbort("merchant validation error");
            settle({
              success: false,
              error: `Merchant validation error: ${error}`,
            });
          }
        };

        // Handle payment authorization (user approved with Face ID/Touch ID)
        session.onpaymentauthorized = (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
          console.log("[ApplePay:Airwallex] onpaymentauthorized called");
          phase = "authorized";

          // Extract the encrypted token
          const payment = event.payment;
          const token: ApplePayEncryptedToken = {
            paymentData: payment.token.paymentData as ApplePayEncryptedToken["paymentData"],
            paymentMethod: {
              displayName: payment.token.paymentMethod.displayName,
              network: payment.token.paymentMethod.network,
              type: payment.token.paymentMethod.type,
            },
            transactionIdentifier: payment.token.transactionIdentifier,
          };

          // Apple sends absent billing-contact fields as "" rather than undefined, so `??`
          // would stop on the empty billing name and never reach the shipping contact.
          // Take the name as a pair from one contact, so a billing first name is never
          // joined to a shipping last name.
          const payerEmail = payment.billingContact?.emailAddress || payment.shippingContact?.emailAddress || undefined;
          const billing = payment.billingContact;
          const nameSource = billing?.givenName || billing?.familyName ? billing : payment.shippingContact;
          const payerFirstName = nameSource?.givenName || undefined;
          const payerLastName = nameSource?.familyName || undefined;
          // PAY-1953: billing address (incl. zip) from the Apple Pay sheet's billing contact.
          const payerBillingAddress = fromApplePayContact(payment.billingContact);
          // Only when asked: without postalAddress the shipping contact holds no address.
          const payerShippingAddress = config.requestShippingAddress
            ? fromApplePayContact(payment.shippingContact)
            : undefined;

          // Create the complete callback that the caller will use after confirming
          const complete = (status: "success" | "fail") => {
            console.log("[ApplePay:Airwallex] completing session with status:", status);
            const appleStatus = status === "success" ? STATUS_SUCCESS : STATUS_FAILURE;
            try {
              session.completePayment({ status: appleStatus });
            } catch (error) {
              // Apple closes the sheet itself if /confirm takes too long; the charge
              // outcome is already decided by then, so this is log-only.
              console.warn("[ApplePay:Airwallex] completePayment() rejected: session no longer active", error);
            }
          };

          settle({
            success: true,
            token,
            payerEmail,
            payerFirstName,
            payerLastName,
            payerBillingAddress,
            payerShippingAddress,
            complete,
          });
        };

        // Handle cancellation. Fires for a user tap on the sheet's X AND for cancels
        // Apple initiates (failed domain/merchant validation, timeouts). Newer WebKit
        // attaches `sessionError` with the reason; record it so prod can tell the two
        // apart — most prod cancels land ~1s after /start returns, before we ever
        // complete validation, and it is not yet known which side closes the sheet.
        session.oncancel = (event?: ApplePayJS.Event) => {
          cancelled = true;
          const sessionError = (event as { sessionError?: { code?: string; message?: string } } | undefined)
            ?.sessionError;
          console.log("[ApplePay:Airwallex] oncancel", { phase, sessionError });
          AnalyticsService.capture("apple_pay_sheet_cancelled", {
            processor: "airwallex",
            phase,
            session_error_code: sessionError?.code ?? null,
            session_error_message: sessionError?.message ?? null,
          });
          settle({ success: false, cancelled: true });
        };

        // Start the session - this shows the Apple Pay sheet
        console.log("[ApplePay:Airwallex] Starting Apple Pay session");
        session.begin();
      } catch (error) {
        console.error("[ApplePay:Airwallex] Session creation error:", error);
        resolve({
          success: false,
          error: `Failed to create Apple Pay session: ${error}`,
        });
      }
    });
  }
}
